import { memo } from 'react';
import { css, CSSObject } from '@emotion/react';
import { UseFloatingReturn } from '@floating-ui/react';
import { Emoji } from 'emoji-picker-react';
import { humanizeDateTime } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Text, styles as dsStyles } from '@frontend/design-system';
import { DEFAULT_STATUS_EMOJI_UNIFIED } from '../../../constants';
import { useTeamChatSelector } from '../../../providers/team-chat.provider';

interface Props {
  name: string;
  date: string;
  userID: string;
  css?: CSSObject;
  tooltipTriggerProps?: { ref: UseFloatingReturn['refs']['setReference'] };
}

export const Author = memo(({ date, name, userID, css, tooltipTriggerProps }: Props) => {
  const user = useTeamChatSelector((ctx) => ctx.helpers.getUser(userID));
  const emojiText = user?.status?.status?.emoji;
  const hasStatus = !!user?.status?.status?.text || !!user?.status?.status?.emoji;
  const isDeactivated = Boolean(user?.isDeactivated);

  return (
    <div css={[styles.wrapper, css]} {...(tooltipTriggerProps && { ...tooltipTriggerProps })}>
      <Text css={[dsStyles.truncate, styles.name, isDeactivated && styles.nameDeactivated]} weight='bold'>
        {name}
      </Text>
      {!!hasStatus &&
        (emojiText ? (
          <Text css={styles.statusEmoji}>{emojiText}</Text>
        ) : (
          <Emoji unified={DEFAULT_STATUS_EMOJI_UNIFIED} size={20} />
        ))}
      <Text as='time' color='subdued' size='medium'>
        {humanizeDateTime(date)}
      </Text>
    </div>
  );
});

Author.displayName = 'Author';

const styles = {
  wrapper: css`
    grid-column: 2/3;
    grid-row: 1/2;
    align-items: center;
    display: flex;
    gap: ${theme.spacing(1)};
    width: fit-content;
  `,
  statusEmoji: css({
    minWidth: '24px',
    fontSize: theme.fontSize(20),
    alignSelf: 'baseline',
  }),
  name: css`
    white-space: normal;
  `,
  nameDeactivated: css`
    color: ${theme.colors.neutral50};
  `,
};
